import { Directive, ElementRef, Input, OnChanges, SimpleChanges, AfterViewInit, ComponentRef, ViewContainerRef, NgZone, Output, EventEmitter } from '@angular/core';
import embed from 'vega-embed';

declare const Diff2Html: any;
declare const markmap: any;
declare const vis: any;

/**
 * Directive to enhance markdown output with special renderers:
 * - vega-lite / vega: Interactive charts
 * - diff: Side-by-side code diffs
 * - markmap: Mind maps
 * - timeline: Interactive timelines
 * - js:runnable / py:runnable: Code playgrounds
 */
@Directive({
    selector: '[appMarkdownExtensions]',
    standalone: false
})
export class MarkdownExtensionsDirective implements AfterViewInit, OnChanges {
  @Input() appMarkdownExtensions: string = '';
  /** Folder that relative paths in inline code resolve against (the channel's
   *  working directory). Without it only absolute and ~ paths are linkified. */
  @Input() markdownBasePath: string | null | undefined;
  @Output() openMarkdownFile = new EventEmitter<{ path: string; title: string }>();

  /** A whole inline-code span that is a bare relative path: at least one
   *  directory, an extension, no spaces, not starting with / ~ or a scheme.
   *  Confined to <code> so prose like "and/or" or "km/h" never matches. */
  private static readonly RELATIVE_CODE_PATH = /^(?:\.\.?\/)?(?:[\w.@+-]+\/)+[\w.@+-]+\.\w+$/;

  constructor(private el: ElementRef, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.processCodeBlocks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appMarkdownExtensions']) {
      // Wait for DOM update - run outside zone to avoid triggering CD
      // Use multiple attempts since markdown component renders asynchronously
      this.ngZone.runOutsideAngular(() => {
        // Try immediately, then after short delays to catch async rendering
        setTimeout(() => this.processCodeBlocks(), 0);
        setTimeout(() => this.processCodeBlocks(), 100);
        setTimeout(() => this.processCodeBlocks(), 500);
      });
    }
  }

  private processCodeBlocks(): void {
    const container = this.el.nativeElement;

    // Process markdown file paths to make them clickable
    this.processMarkdownPaths(container);

    // Intercept file upload link clicks to open in-app viewers
    container.querySelectorAll('a[href*="/channel-uploads/"], a[href*="/uploads/"]').forEach((link: Element) => {
      if (link.getAttribute('data-upload-processed')) return;
      link.setAttribute('data-upload-processed', 'true');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href') || '';
        const title = link.textContent?.trim() || href.split('/').pop() || 'file';
        this.ngZone.run(() => {
          this.openMarkdownFile.emit({ path: href, title });
        });
      });
    });

    // Only select code blocks that haven't been processed yet
    const codeBlocks = container.querySelectorAll('pre code:not([data-processed])');

    codeBlocks.forEach((block: HTMLElement) => {
      const language = this.getLanguage(block);

      if (language === 'vega-lite' || language === 'vega') {
        block.setAttribute('data-processed', 'true');
        this.renderVegaLite(block);
      } else if (language === 'diff') {
        block.setAttribute('data-processed', 'true');
        this.renderDiff(block);
      } else if (language === 'markmap') {
        block.setAttribute('data-processed', 'true');
        this.renderMarkmap(block);
      } else if (language === 'timeline') {
        block.setAttribute('data-processed', 'true');
        this.renderTimeline(block);
      } else if (language === 'js:runnable' || language === 'javascript:runnable') {
        block.setAttribute('data-processed', 'true');
        this.renderJsPlayground(block);
      } else if (language === 'py:runnable' || language === 'python:runnable') {
        block.setAttribute('data-processed', 'true');
        this.renderPyPlayground(block);
      }
    });
  }

  private getLanguage(block: HTMLElement): string {
    const match = block.className.match(/language-([\w:-]+)/);
    return match ? match[1] : '';
  }

  private async renderVegaLite(block: HTMLElement): Promise<void> {
    try {
      const spec = JSON.parse(block.textContent || '{}');
      const preElement = block.parentElement;
      if (!preElement) return;

      const container = document.createElement('div');
      container.className = 'vega-viz-container';

      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

      await embed(container, spec, {
        actions: { export: true, source: false, compiled: false, editor: false },
        theme: isDark ? 'dark' : 'vox'
      });

      preElement.parentElement?.replaceChild(container, preElement);
    } catch (error) {
      console.error('Failed to render Vega chart:', error);
    }
  }

  private renderDiff(block: HTMLElement): void {
    try {
      if (typeof Diff2Html === 'undefined') {
        console.warn('Diff2Html not loaded');
        return;
      }

      const diffContent = block.textContent || '';
      const preElement = block.parentElement;
      if (!preElement) return;

      const container = document.createElement('div');
      container.className = 'diff-container';

      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

      const html = Diff2Html.html(diffContent, {
        drawFileList: false,
        matching: 'lines',
        outputFormat: 'side-by-side',
        highlight: true
      });

      container.innerHTML = html;
      container.classList.add(isDark ? 'diff-theme-dark' : 'diff-theme-light');

      preElement.parentElement?.replaceChild(container, preElement);
    } catch (error) {
      console.error('Failed to render diff:', error);
    }
  }

  private renderMarkmap(block: HTMLElement): void {
    try {
      if (typeof markmap === 'undefined' || !markmap.Markmap || !markmap.Transformer) {
        console.warn('Markmap not loaded');
        return;
      }

      const markdownContent = block.textContent || '';
      const preElement = block.parentElement;
      if (!preElement) return;

      const container = document.createElement('div');
      container.className = 'markmap-container';
      container.style.width = '100%';
      container.style.height = '400px';

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '400');
      container.appendChild(svg);

      preElement.parentElement?.replaceChild(container, preElement);

      // Parse and render using the Transformer class
      const transformer = new markmap.Transformer();
      const { root } = transformer.transform(markdownContent);
      markmap.Markmap.create(svg, {}, root);
    } catch (error) {
      console.error('Failed to render markmap:', error);
    }
  }

  private renderTimeline(block: HTMLElement): void {
    try {
      if (typeof vis === 'undefined' || !vis.Timeline) {
        console.warn('vis-timeline not loaded');
        return;
      }

      const timelineData = JSON.parse(block.textContent || '{}');
      const preElement = block.parentElement;
      if (!preElement) return;

      const container = document.createElement('div');
      container.className = 'timeline-container';
      container.style.height = '300px';

      preElement.parentElement?.replaceChild(container, preElement);

      const items = new vis.DataSet(timelineData.items || []);
      const groups = timelineData.groups ? new vis.DataSet(timelineData.groups) : null;

      new vis.Timeline(container, items, groups, {
        ...timelineData.options,
        editable: false,
        zoomable: true,
        moveable: true
      });
    } catch (error) {
      console.error('Failed to render timeline:', error);
    }
  }

  private renderJsPlayground(block: HTMLElement): void {
    this.renderPlayground(block, 'javascript');
  }

  private renderPyPlayground(block: HTMLElement): void {
    this.renderPlayground(block, 'python');
  }

  private renderPlayground(block: HTMLElement, language: string): void {
    const code = block.textContent || '';
    const preElement = block.parentElement;
    if (!preElement) return;

    const playground = document.createElement('div');
    playground.className = 'code-playground';
    playground.innerHTML = `
      <div class="playground-toolbar">
        <button class="playground-run-btn">▶ Run ${language === 'python' ? 'Python' : ''}</button>
        <button class="playground-clear-btn">Clear</button>
      </div>
      <textarea class="playground-editor" spellcheck="false">${this.escapeHtml(code)}</textarea>
      <div class="playground-output">
        <div class="output-placeholder">Press Run to execute code</div>
      </div>
    `;

    const editor = playground.querySelector('.playground-editor') as HTMLTextAreaElement;
    const output = playground.querySelector('.playground-output') as HTMLElement;
    const runBtn = playground.querySelector('.playground-run-btn') as HTMLButtonElement;
    const clearBtn = playground.querySelector('.playground-clear-btn') as HTMLButtonElement;

    runBtn.addEventListener('click', () => {
      if (language === 'javascript') {
        this.executeJavaScript(editor.value, output);
      } else {
        output.innerHTML = '<div class="output-placeholder">Python execution requires Pyodide (not loaded)</div>';
      }
    });

    clearBtn.addEventListener('click', () => {
      output.innerHTML = '<div class="output-placeholder">Output cleared</div>';
    });

    preElement.parentElement?.replaceChild(playground, preElement);
  }

  private executeJavaScript(code: string, output: HTMLElement): void {
    output.innerHTML = '';
    const logs: string[] = [];

    const originalLog = console.log;
    console.log = (...args) => {
      logs.push(args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a)).join(' '));
      originalLog.apply(console, args);
    };

    try {
      const result = new Function(code)();
      logs.forEach(log => {
        const div = document.createElement('div');
        div.className = 'output-line output-log';
        div.textContent = log;
        output.appendChild(div);
      });

      if (result !== undefined && logs.length === 0) {
        const div = document.createElement('div');
        div.className = 'output-line output-result';
        div.textContent = typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result);
        output.appendChild(div);
      }

      if (logs.length === 0 && result === undefined) {
        output.innerHTML = '<div class="output-placeholder">Code executed (no output)</div>';
      }
    } catch (error: any) {
      const div = document.createElement('div');
      div.className = 'output-line output-error';
      div.textContent = `Error: ${error.message}`;
      output.appendChild(div);
    } finally {
      console.log = originalLog;
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Find local file paths in text content and make them clickable.
   * Matches absolute paths like /home/user/file.ext or ~/docs/file.ext
   */
  private processMarkdownPaths(container: HTMLElement): void {
    // Negative lookbehind (?<![:/\w]) prevents matching paths inside URLs
    // e.g. https://example.com/path/file.html — the /path/... is preceded by a word char
    const filePathRegex = /(?<![:/\w])((?:\/[\w.@+-]+)+\.\w+|~(?:\/[\w.@+-]+)+\.\w+)/g;

    // Find all text nodes that might contain file paths
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // Skip if already inside our button, a link, or a multi-line code block
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (parent.closest('.md-path-btn') || parent.closest('pre') || parent.closest('a')) {
            return NodeFilter.FILTER_REJECT;
          }
          if (filePathRegex.test(node.textContent || '')) {
            filePathRegex.lastIndex = 0;
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_REJECT;
        }
      }
    );

    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    // Relative paths: only when an inline <code> span IS the path, and only
    // when we know what to resolve it against.
    const base = (this.markdownBasePath || '').replace(/\/+$/, '');
    if (base) {
      container.querySelectorAll('code:not([data-relpath-processed])').forEach((code: Element) => {
        if (code.closest('pre') || code.closest('a') || code.children.length) return;
        const text = (code.textContent || '').trim();
        if (!MarkdownExtensionsDirective.RELATIVE_CODE_PATH.test(text)) return;
        code.setAttribute('data-relpath-processed', 'true');
        const resolved = `${base}/${text.replace(/^\.\//, '')}`;
        code.parentElement?.replaceChild(this.pathLink(text, resolved), code);
      });
    }

    textNodes.forEach(textNode => {
      const text = textNode.textContent || '';
      const parent = textNode.parentElement;
      if (!parent) return;

      const matches: { path: string; start: number; end: number }[] = [];
      let match;
      filePathRegex.lastIndex = 0;
      while ((match = filePathRegex.exec(text)) !== null) {
        matches.push({
          path: match[1],
          start: match.index,
          end: match.index + match[0].length
        });
      }

      if (matches.length === 0) return;

      const fragment = document.createDocumentFragment();
      let lastEnd = 0;

      matches.forEach(m => {
        if (m.start > lastEnd) {
          fragment.appendChild(document.createTextNode(text.slice(lastEnd, m.start)));
        }

        fragment.appendChild(this.pathLink(m.path, m.path));
        lastEnd = m.end;
      });

      if (lastEnd < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastEnd)));
      }

      // If parent is a <code> element, replace the whole <code> with the fragment
      // (keeps the link styling clean without nested code formatting)
      if (parent.tagName === 'CODE' && parent.parentElement) {
        parent.parentElement.replaceChild(fragment, parent);
      } else {
        parent.replaceChild(fragment, textNode);
      }
    });
  }

  /** An anchor-styled link for a file path: shows `label`, opens `path`. */
  private pathLink(label: string, path: string): HTMLElement {
    const a = document.createElement('a');
    a.className = 'md-path-btn';
    a.textContent = label;
    a.title = path === label ? 'Open file' : path;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const title = path.split('/').pop() || 'file';
      this.ngZone.run(() => {
        this.openMarkdownFile.emit({ path, title });
      });
    });
    return a;
  }
}
